import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import { toast } from '../lib/notification';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** When true, a 403 response will NOT trigger a toast. Use for queries
     *  whose permission denial should be handled silently by the component. */
    _suppressForbiddenToast?: boolean;
    /** When true, ALL error toasts (403, 5xx) are suppressed. Use for queries
     *  whose errors are handled inline by QueryStateView / CursorListStateView
     *  or for optional/picker queries where a toast would be disruptive. */
    _suppressErrorToast?: boolean;
  }
}

/**
 * API Error response shape
 */
export interface ApiValidationError {
  field: string;
  message: string;
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
  errors?: ApiValidationError[];
}

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  data: T;
  message?: string;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
  };
}

/**
 * Session state key — tracks whether we believe we have a valid cookie session.
 * Actual tokens are in HTTP-only cookies (invisible to JS).
 */
const SESSION_KEY = 'auth_session_active';

/**
 * Token manager — now just tracks session presence since actual tokens are in HTTP-only cookies.
 */
export const tokenManager = {
  getAccessToken: (): string | null => null, // Tokens are in HTTP-only cookies
  getRefreshToken: (): string | null => null,

  setTokens: (_accessToken?: string, _refreshToken?: string) => {
    localStorage.setItem(SESSION_KEY, '1');
  },

  clearTokens: () => {
    localStorage.removeItem(SESSION_KEY);
  },

  hasTokens: () => !!localStorage.getItem(SESSION_KEY),
};

/**
 * Request queue for handling token refresh
 */
interface QueuedRequest {
  resolve: (value?: unknown) => void;
  reject: (error: unknown) => void;
}

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];
let isRedirectingToLogin = false;

const processQueue = (error: unknown) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

/**
 * Reset interceptor state after a successful login. The module-level flags
 * survive SPA navigation, so a previous failed-refresh cycle leaves
 * `isRedirectingToLogin = true` which silently swallows all future 401s.
 */
export function resetAuthInterceptorState(): void {
  isRedirectingToLogin = false;
  isRefreshing = false;
  failedQueue = [];
}

let _configuredBaseURL = '/api';

export interface ApiConfig {
  baseURL: string;
}

/**
 * Configure the API base URL. Call this once at app startup before any API calls.
 */
export function configureApi(config: ApiConfig): void {
  _configuredBaseURL = config.baseURL;
  // Recreate the singleton client with the new base URL
  _apiClient = createApiClient(_configuredBaseURL);
  _apiHelpers = createApiHelpers(_apiClient);
}

/**
 * Refresh access token — BFF reads the refresh token from the HTTP-only cookie,
 * validates it, and sets new cookies in the response.
 */
async function refreshAccessToken(): Promise<void> {
  await axios.post(
    `${_configuredBaseURL}/v1/auth/refresh`,
    {},
    { withCredentials: true }
  );
}

/**
 * Create a configured Axios instance.
 *
 * @param baseURL - Optional base URL override. Falls back to configured base URL.
 */
export function createApiClient(baseURL?: string): AxiosInstance {
  const client = axios.create({
    baseURL: baseURL || _configuredBaseURL,
    timeout: 30000,
    withCredentials: true, // Send cookies with every request
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Response interceptor - handle errors and token refresh
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
        _suppressForbiddenToast?: boolean;
        _suppressErrorToast?: boolean;
      };

      // Handle 401 Unauthorized - Token expired (skip for login — 401 there means bad credentials)
      const isLoginRequest = originalRequest.url?.includes('/auth/login');
      if (error.response?.status === 401 && !originalRequest._retry && !isLoginRequest) {
        if (isRedirectingToLogin) {
          return Promise.reject(error);
        }

        // Avoid infinite loop on refresh endpoint
        if (originalRequest.url?.includes('/auth/refresh')) {
          isRedirectingToLogin = true;
          tokenManager.clearTokens();
          toast.error('Session expired. Please login again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => client(originalRequest))
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          await refreshAccessToken();
          processQueue(null);
          // Retry original request — new cookie is already set by browser
          return client(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          isRedirectingToLogin = true;
          tokenManager.clearTokens();
          toast.error('Session expired. Please login again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Handle other errors — prefer nested data.message (the actual error) over the envelope message
      const respData = error.response?.data as Record<string, unknown> | undefined;
      const nestedData = respData?.data as Record<string, unknown> | undefined;
      const nestedMessage = nestedData?.message as string | undefined;
      const validationErrors = (nestedData?.errors ?? respData?.errors) as ApiValidationError[] | undefined;
      const apiError: ApiError = {
        message: nestedMessage || respData?.message as string || error.message || 'An unexpected error occurred',
        code: (respData?.code as string) || error.code,
        status: error.response?.status,
        details: respData?.details as Record<string, unknown> | undefined,
        errors: Array.isArray(validationErrors) ? validationErrors : undefined,
      };

      const suppressAll = originalRequest._suppressErrorToast;

      if (error.response?.status === 403) {
        if (!suppressAll && !originalRequest._suppressForbiddenToast) {
          toast.error('You don\'t have permission to perform this action.');
        }
        return Promise.reject(apiError);
      } else if (error.response?.status === 404) {
        // Let the caller handle 404
      } else if (error.response?.status && error.response.status >= 500) {
        if (!suppressAll) {
          toast.error('Server error. Please try again later.');
        }
      }

      return Promise.reject(apiError);
    }
  );

  return client;
}

/**
 * Build type-safe request helpers around an Axios instance.
 */
function createApiHelpers(client: AxiosInstance) {
  return {
    get: <T>(url: string, config?: AxiosRequestConfig) =>
      client.get<ApiResponse<T>>(url, config).then((res) => res.data),

    post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
      client.post<ApiResponse<T>>(url, data, config).then((res) => res.data),

    put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
      client.put<ApiResponse<T>>(url, data, config).then((res) => res.data),

    patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
      client.patch<ApiResponse<T>>(url, data, config).then((res) => res.data),

    delete: <T>(url: string, config?: AxiosRequestConfig) =>
      client.delete<ApiResponse<T>>(url, config).then((res) => res.data),
  };
}

let _apiClient = createApiClient();
let _apiHelpers = createApiHelpers(_apiClient);

/**
 * Singleton API client instance (uses configured base URL)
 */
export function getApiClient(): AxiosInstance {
  return _apiClient;
}

/**
 * Type-safe API request helpers (default instance)
 */
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) => _apiHelpers.get<T>(url, config),
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => _apiHelpers.post<T>(url, data, config),
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => _apiHelpers.put<T>(url, data, config),
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => _apiHelpers.patch<T>(url, data, config),
  delete: <T>(url: string, config?: AxiosRequestConfig) => _apiHelpers.delete<T>(url, config),
};

/** @deprecated Use getApiClient() instead */
export const apiClient = _apiClient;

/**
 * Create a separate API client + helpers with a custom base URL.
 */
export function createModuleApi(baseURL?: string) {
  const client = createApiClient(baseURL);
  return { client, api: createApiHelpers(client) };
}

/**
 * Query key factory for consistent key management
 */
export const queryKeys = {
  // Bootstrap
  bootstrap: (tenantId: string | null) => ['bootstrap', tenantId] as const,

  // Auth
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    session: () => [...queryKeys.auth.all, 'session'] as const,
  },

  // Tenant
  tenant: {
    all: ['tenant'] as const,
    config: (tenantId: string) => [...queryKeys.tenant.all, tenantId] as const,
  },

  // Theme
  theme: (tenantId?: string) => ['theme', tenantId] as const,
} as const;
